/**
 * TenantContextHeader
 * Displays the current organization being managed with session lock status
 * Shows persistent header on all settings pages
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ChevronRight,
  Lock,
  X,
} from 'lucide-react';
import { CARD, CARD_CLASS, Button as KitButton } from '@shared/ui/kit';
import {
  Backdrop,
  Dialog,
  DialogFoot,
  DialogHead,
} from './chrome';
import { useTenantContextStore, useTenantAccentColor } from '@stores/tenantContextStore';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { useAuthStore } from '@stores/authStore';

interface TenantContextHeaderProps {
  showBackButton?: boolean;
  exitPath?: string;
}

export function TenantContextHeader({ showBackButton = true, exitPath = '/settings/tenants' }: TenantContextHeaderProps) {
  const navigate = useNavigate();
  const { currentTenant, isLocked, lockedAt, unlockSession, clearTenant } =
    useTenantContextStore();
  const accentColor = useTenantAccentColor();
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);

  if (!currentTenant) {
    return null;
  }

  const formatLockedTime = (isoString: string | null) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleUnlockClick = () => {
    setShowUnlockConfirm(true);
  };

  const handleConfirmUnlock = () => {
    unlockSession();
    clearTenant();
    setShowUnlockConfirm(false);
    navigate(exitPath);
  };

  const handleCancelUnlock = () => {
    setShowUnlockConfirm(false);
  };

  return (
    <>
      {/* Organization Context Header.
          A card on the canvas, like the rail and the content column beneath it —
          not a band welded to the window edge. It used to be the one full-bleed
          surface on the page, which made it read as browser chrome rather than as
          part of the thing being edited.

          Flat raised ground, not the old gradient into white: a second ground
          fading underneath the card read as a seam rather than as a header.

          The left edge keeps the TENANT'S OWN accent colour, and it is the only
          place in the product that colour is allowed to appear. That is the whole
          point of it — a superadmin working inside someone else's organization
          needs one signal that says *whose* at a glance, and it must not be a
          colour the theme also uses for status. */}
      <div
        className={`flex-none overflow-hidden ${CARD_CLASS}`}
        style={{
          ...CARD,
          borderLeftColor: accentColor,
          borderLeftWidth: '3px',
        }}
      >
        <div className="flex items-center gap-[14px] px-[18px] py-[10px]">
          {/* Organization icon/logo */}
          <div
            className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-[9px]"
            style={{ backgroundColor: `${accentColor}1F` }}
          >
            {currentTenant.logoUrl ? (
              <img
                src={currentTenant.logoUrl}
                alt={currentTenant.name}
                className="h-7 w-7 rounded-[6px] object-cover"
              />
            ) : (
              <Building2
                className="h-[18px] w-[18px]"
                style={{ color: accentColor }}
                aria-hidden="true"
              />
            )}
          </div>

          {/* Identity. The eyebrow is doing real work — it is the difference
              between "my settings" and "someone else's settings", which is the
              one thing a superadmin must not misread. */}
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-[9px]">
              <span className="co-mono flex-none text-[9px] font-semibold uppercase tracking-[.18em] text-[var(--co-ink-4)]">
                Managing
              </span>
              <span
                className="truncate text-[17px] font-bold leading-[1.15] tracking-[-.015em] text-[var(--co-ink)]"
                style={{ fontFamily: 'var(--co-font-display)' }}
              >
                {currentTenant.name}
              </span>
            </div>

            {/* Slug and plan are FACTS, so they sit in the meta line in mono
                rather than each wearing a chip. Two chips side by side made the
                plan look like a state worth watching, which left the session
                state — the only thing here that can change — competing with it. */}
            <div className="co-mono mt-[3px] flex items-center gap-[7px] text-[10px] tracking-[.06em] text-[var(--co-ink-3)]">
              <span className="truncate">{currentTenant.slug}</span>
              {currentTenant.plan && (
                <>
                  <span className="text-[var(--co-line-strong)]" aria-hidden="true">
                    /
                  </span>
                  <span className="uppercase tracking-[.1em]">{currentTenant.plan}</span>
                </>
              )}
            </div>
          </div>

          {/* Session state.
              A solid dot in the full tone, not a 10%-alpha wash on its own: at
              chip size the wash reads as another beige pill, and this is the one
              element on the band whose colour has to be unmistakable. The dot
              carries the hue, the border and ground stay quiet.

              `isLocked` is the store's word for "this session is bound to this
              tenant", i.e. you can edit — hence EDITING, with the moment the
              binding was taken beside it rather than buried in the meta line. */}
          <div
            className="flex flex-none items-center gap-[9px] rounded-lg border px-[11px] py-[6px]"
            style={{
              borderColor: isLocked ? 'var(--co-green-line)' : 'var(--co-terracotta-line)',
              background: isLocked ? 'var(--co-green-wash)' : 'var(--co-terracotta-wash)',
            }}
          >
            <span
              className="h-[7px] w-[7px] flex-none rounded-full"
              style={{ background: isLocked ? 'var(--co-green)' : 'var(--co-terracotta)' }}
              aria-hidden="true"
            />
            <span
              className="co-mono text-[9.5px] font-semibold uppercase tracking-[.12em]"
              style={{
                color: isLocked ? 'var(--co-green-ink)' : 'var(--co-terracotta-ink)',
              }}
            >
              {isLocked ? 'Editing' : 'Locked'}
            </span>
            {isLocked && lockedAt && (
              <span className="co-mono whitespace-nowrap text-[9.5px] tracking-[.08em] text-[var(--co-ink-3)]">
                since {formatLockedTime(lockedAt)}
              </span>
            )}
          </div>

          {/* Exit/Switch button. Platform route only — a tenant admin has
              exactly one organization and nothing to exit to. */}
          {showBackButton && (
            <KitButton onClick={handleUnlockClick}>
              <X className="h-[15px] w-[15px]" aria-hidden="true" />
              Exit organization settings
            </KitButton>
          )}

          {/* Connection state + notifications live in this header on the
              in-app (tenant admin) route, where it is the page header — so
              MainLayout's fallback row above the card is not rendered. The
              platform route renders full screen outside MainLayout and has no
              such row, so it is left as it was. */}
          {!showBackButton && (
            <>
              <span aria-hidden="true" className="h-5 w-px flex-none bg-[var(--co-line)]" />
              <PageHeaderMeta className="flex-none" />
            </>
          )}
        </div>
      </div>

      {/* Unlock Confirmation Modal */}
      {showUnlockConfirm && (
        <Backdrop>
          <Dialog labelledBy="exit-org-settings-title" onClose={handleCancelUnlock}>
            <DialogHead
              id="exit-org-settings-title"
              title="Exit organization settings?"
              tone="warn"
              onClose={handleCancelUnlock}
            />
            <div className="px-6 py-5">
              <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]">
                You are currently managing settings for:
              </p>
              <div className="mt-3 flex items-center gap-3 rounded-lg border border-[var(--co-line)] bg-[var(--co-sunken)] px-3 py-[10px]">
                <Building2
                  className="h-[15px] w-[15px] flex-none text-[var(--co-ink-4)]"
                  aria-hidden="true"
                />
                <span className="truncate text-[13px] font-semibold text-[var(--co-ink)]">
                  {currentTenant.name}
                </span>
              </div>
              <p className="mt-4 text-[12px] leading-[1.55] text-[var(--co-ink-3)]">
                This locks the session. Any unsaved changes will be lost, and you will be
                redirected to the organizations list.
              </p>
            </div>
            <DialogFoot>
              <KitButton onClick={handleCancelUnlock}>Cancel</KitButton>
              <KitButton variant="destructive" onClick={handleConfirmUnlock}>
                Exit &amp; lock
              </KitButton>
            </DialogFoot>
          </Dialog>
        </Backdrop>
      )}
    </>
  );
}

/**
 * TenantRequiredGuard
 * Redirects to tenant selection if no tenant is selected
 */
export function TenantRequiredGuard({ children, exitPath = '/settings/tenants' }: { children: React.ReactNode; exitPath?: string }) {
  const navigate = useNavigate();
  const { currentTenant, _hasHydrated } = useTenantContextStore();
  const role = useAuthStore((state) => state.user?.role);

  // Wait for hydration
  if (!_hasHydrated) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[var(--co-brass)]" />
      </div>
    );
  }

  // An agent has no organisation to pick: the server refuses these pages for
  // the role (403), which leaves no tenant loaded. "No organization selected"
  // sent them on into an admin area; say what is actually true instead.
  if (!currentTenant && (role === 'agent' || role === 'light_agent')) {
    return (
      <div className="flex h-64 flex-col items-center justify-center px-6 text-center">
        <Lock className="mb-4 h-10 w-10 text-[var(--co-ink-4)]" aria-hidden="true" />
        <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">
          You don&apos;t have access to organisation settings
        </h3>
        <p className="mx-auto mt-[6px] max-w-[46ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
          These pages are for administrators. Ask an admin if you need something changed here.
        </p>
        <div className="mt-5">
          <KitButton variant="primary" onClick={() => navigate('/settings/account')}>
            Back to your settings
          </KitButton>
        </div>
      </div>
    );
  }

  // Redirect if no tenant selected
  if (!currentTenant) {
    return (
      <div className="flex h-64 flex-col items-center justify-center px-6 text-center">
        <Building2 className="mb-4 h-10 w-10 text-[var(--co-ink-4)]" aria-hidden="true" />
        <h3 className="text-[15px] font-semibold text-[var(--co-ink)]">
          No organization selected
        </h3>
        <p className="mx-auto mt-[6px] max-w-[46ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
          Every section of this page reads from one organization. Pick which one to manage
          and they all resolve together.
        </p>
        <div className="mt-5">
          <KitButton variant="primary" onClick={() => navigate(exitPath)}>
            Go to organizations
            <ChevronRight className="h-[15px] w-[15px]" aria-hidden="true" />
          </KitButton>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default TenantContextHeader;
